/* Step 2 — Building configuration: floors, BHK, extras, Vastu toggle. */
import React from "react";
import { ChevronLeft, ChevronRight, Home, Bed, Plus, Check } from "lucide-react";

const FLOORS = [
  { id: "G", label: "Ground floor" },
  { id: "G+1", label: "G + 1 (Most popular)" },
  { id: "G+2", label: "G + 2" },
  { id: "duplex", label: "Duplex" },
];

const BHKS = [1, 2, 3, 4, 5];

const EXTRAS = [
  { id: "parking", label: "Car Parking" },
  { id: "pooja", label: "Pooja Room" },
  { id: "study", label: "Study Room" },
  { id: "home_office", label: "Home Office" },
  { id: "store", label: "Store Room" },
  { id: "balcony", label: "Balcony" },
  { id: "lift", label: "Lift" },
  { id: "servant", label: "Servant Room" },
  { id: "terrace_garden", label: "Terrace Garden" },
];

const Toggle = ({ checked, onChange, testId, children }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    data-testid={testId}
    className={`w-full rounded-2xl border-2 px-3 py-3 text-left transition-colors flex items-center gap-2 ${
      checked
        ? "bg-navy text-white border-navy"
        : "bg-white text-navy border-border hover:border-navy/40"
    }`}
  >
    <span className={`h-5 w-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
      checked ? "bg-gold border-gold" : "border-border bg-white"
    }`}>
      {checked && <Check className="h-3.5 w-3.5 text-navy" />}
    </span>
    <span className="text-sm font-bold">{children}</span>
  </button>
);

export default function StepBuilding({ value, onChange, onBack, onNext }) {
  const toggleExtra = (id) => {
    const has = value.extras.includes(id);
    onChange({
      ...value,
      extras: has ? value.extras.filter((x) => x !== id) : [...value.extras, id],
    });
  };

  return (
    <section data-testid="studio-step-building">
      <div className="text-[11px] uppercase tracking-[0.2em] text-gold-dark font-bold">Step 2 of 3</div>
      <h2 className="mt-1 text-2xl sm:text-3xl font-extrabold text-navy">Building configuration</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Tell us how many floors, bedrooms, and which extras matter.
      </p>

      {/* Floors */}
      <div className="mt-5 rounded-2xl bg-white border-2 border-border p-4">
        <div className="flex items-center gap-2">
          <Home className="h-4 w-4 text-navy" />
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-bold">
            Number of floors
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {FLOORS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => onChange({ ...value, floors: f.id })}
              data-testid={`studio-floors-${f.id}`}
              className={`rounded-2xl border-2 px-3 py-3 text-left transition-colors ${
                value.floors === f.id
                  ? "bg-navy text-white border-navy"
                  : "bg-white text-navy border-border hover:border-navy/40"
              }`}
            >
              <div className="text-base font-extrabold">{f.id}</div>
              <div className={`text-[10.5px] uppercase tracking-wider font-bold mt-0.5 ${
                value.floors === f.id ? "text-gold" : "text-muted-foreground"
              }`}>
                {f.label}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* BHK */}
      <div className="mt-4 rounded-2xl bg-white border-2 border-border p-4">
        <div className="flex items-center gap-2">
          <Bed className="h-4 w-4 text-navy" />
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-bold">
            Bedrooms
          </div>
        </div>
        <div className="mt-3 grid grid-cols-5 gap-2">
          {BHKS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange({ ...value, bhk: n })}
              data-testid={`studio-bhk-${n}`}
              className={`rounded-2xl border-2 px-3 py-4 text-center transition-colors ${
                value.bhk === n
                  ? "bg-navy text-white border-navy"
                  : "bg-white text-navy border-border hover:border-navy/40"
              }`}
            >
              <div className="text-2xl font-extrabold tabular-nums">{n}</div>
              <div className={`text-[10.5px] uppercase tracking-wider font-bold mt-0.5 ${
                value.bhk === n ? "text-gold" : "text-muted-foreground"
              }`}>
                BHK
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Extras */}
      <div className="mt-4 rounded-2xl bg-white border-2 border-border p-4">
        <div className="flex items-center gap-2">
          <Plus className="h-4 w-4 text-navy" />
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-bold">
            Additional requirements
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {EXTRAS.map((e) => (
            <Toggle
              key={e.id}
              checked={value.extras.includes(e.id)}
              onChange={() => toggleExtra(e.id)}
              testId={`studio-extra-${e.id}`}
            >
              {e.label}
            </Toggle>
          ))}
        </div>
      </div>

      {/* Vastu */}
      <div className="mt-4 rounded-2xl bg-white border-2 border-border p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-bold">
              Vastu preference
            </div>
            <div className="mt-1 text-sm font-bold text-navy">Apply Vastu-aligned room placements</div>
            <div className="text-[12px] text-muted-foreground mt-0.5">
              Kitchen → SE · Master Bedroom → SW · Pooja → NE
            </div>
          </div>
          <button
            type="button"
            onClick={() => onChange({ ...value, vastu: !value.vastu })}
            data-testid="studio-vastu-toggle"
            className={`shrink-0 h-7 w-12 rounded-full transition-colors relative ${
              value.vastu ? "bg-gold" : "bg-border"
            }`}
            aria-pressed={value.vastu}
          >
            <span className={`absolute top-0.5 ${value.vastu ? "left-6" : "left-0.5"} h-6 w-6 rounded-full bg-white border-2 ${value.vastu ? "border-gold" : "border-border"} transition-all`} />
          </button>
        </div>
      </div>

      <div className="mt-7 flex justify-between">
        <button
          type="button"
          onClick={onBack}
          data-testid="studio-building-back"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border-2 border-border text-navy font-bold hover:border-navy/40"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          data-testid="studio-building-next"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-gold font-extrabold hover:bg-navy-600"
        >
          Generate preview
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </section>
  );
}
